import { addMinutes } from 'date-fns'
import { pipe } from 'fp-ts/lib/function'
import * as R from 'ramda'
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { twMerge } from 'tailwind-merge'
import {
  DayScheduleProps,
  ScheduleEvent,
  ScheduleModalState,
} from './DaySchedule.types'
import { DayScheduleEvent, getDayScheduleLayoutId } from './DayScheduleEvent'
import { DayScheduleEventDetailsModal } from './DayScheduleEventDetailsModal'
import { NewEventPlaceholder } from './NewEventPlaceholder'
import {
  clusterEvents,
  getEventClustersAcrossDays,
} from './schedule.clustering.helpers'
import {
  getColumnGroupAllocation,
  getColumnGroupWeights,
  getColumnHeadings,
  getTotalDayScheduleColumns,
  indentZoneMinWidthPx,
  positionEvents,
} from './schedule.positioning.helpers'
import {
  getDayScheduleHours,
  getDefaultEventDescriptor,
  getEndDayAt,
  getStartDayAt,
  isSameScheduleDay,
  normalizeEventsToTimezone,
  rejectEventsOutsideInterval,
} from './schedule.time.helpers'
import { TZDate } from '@date-fns/tz'
import { formatDateRange, StrictOmit } from '@unimpaired/utils'
import { Button } from '../Button'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { RenderHTMLContent } from '../Text'
import { capitalCase } from 'change-case'

const DayScheduleImpl = <T extends ScheduleEvent>({
  clusterDistance = 1,
  interval: intervalDuration = 15,
  intervalHeight = '1.5rem',
  indentationAmount = 2,
  minEventWidth = 150,
  minColumnWidth,
  maxColumnWidth,
  hideHoursText = false,
  sortColumns = (a, b) => (a > b ? 1 : -1),
  columnHeadingStickyTopOffset,
  forceColumnHeadingToHeightOf,
  midnightOffset = 0,
  noOverlap = false,
  ...props
}: DayScheduleProps<T>) => {
  // Memoize the TZ-normalized event list so downstream memos can depend on
  // its stable reference instead of stringifying the array every render.
  // react-compiler-migration: kept — referenced in eventsThisDay's deps array
  const eventsList = useMemo(
    () => normalizeEventsToTimezone(props.events, props.timezone),
    [props.events, props.timezone],
  )

  // react-compiler-migration: kept — referenced in eventsThisDay's deps array
  const pageDate = useMemo(
    () => new TZDate(props.pageDate || new Date(), props.timezone),
    [props.pageDate?.toISOString(), props.timezone],
  )

  // If pageDate is undefined we don't filter — the caller wants every event
  // react-compiler-migration: kept — referenced in eventClusters/clustersByDay/columnHeadings/columnGroupWeights/positionedDayEvents deps arrays
  const eventsThisDay = useMemo(
    () =>
      props.pageDate
        ? eventsList.filter((event) =>
            isSameScheduleDay(pageDate, event, midnightOffset),
          )
        : eventsList,
    [eventsList, props.pageDate?.toISOString(), pageDate, midnightOffset],
  )

  // todo: bring up getStartDayAt, get events, etc from HourlySchedule to DaySchedule to reduce times run
  // plus give the trimmed events to event clusters to handle events that cross midnight -
  // will also have to somehow do the same for multidaySchedule so that it passes in proper events

  // react-compiler-migration: kept — referenced in clustersByDay's deps array
  const eventClusters = useMemo(
    () =>
      props.clusters ??
      getEventClustersAcrossDays(
        [eventsThisDay],
        [pageDate],
        clusterDistance,
        props.defaultPageStartMidnightOffset,
        props.defaultPageEndMidnightOffset,
      ),
    [
      props.clusters,
      eventsThisDay,
      pageDate,
      clusterDistance,
      props.defaultPageStartMidnightOffset,
      props.defaultPageEndMidnightOffset,
    ],
  )

  const clustersByDay = clusterEvents(
    [eventsThisDay],
    [pageDate],
    eventClusters,
  )

  // react-compiler-migration: kept — referenced in columnGroupWeights's deps array
  const columnHeadings = useMemo(
    () =>
      getColumnHeadings(
        eventsThisDay,
        props.columnateDefaultData,
        props.columnateBy,
        sortColumns,
      ),
    [eventsThisDay, props.columnateDefaultData, props.columnateBy, sortColumns],
  )

  // Per-group weights (max concurrent events); shared by positionEvents, Headings, Separators.
  // react-compiler-migration: kept — referenced in positionedDayEvents's deps array
  const columnGroupWeights = useMemo(
    () =>
      getColumnGroupWeights(eventsThisDay, columnHeadings, props.columnateBy),
    [eventsThisDay, columnHeadings, props.columnateBy],
  )

  // Shared totalCols across events, headings, and separators so tracks align.
  const totalCols = getTotalDayScheduleColumns(columnHeadings?.length ?? 0)

  // Position events once; headings/separator grids share the result for identical track widths.
  // Memoize defaultEventDescriptor so its identity is stable across renders.
  // react-compiler-migration: kept — referenced in positionedDayEvents's deps array
  const defaultEventDescriptor = useMemo(
    () => getDefaultEventDescriptor(props.eventDescriptor),
    [props.eventDescriptor],
  )
  // Sync measurement via useLayoutEffect so first paint has correct width (async → width=0 truncation).
  const outerContainerRef = useRef<HTMLDivElement | null>(null)
  const [measuredWidth, setMeasuredWidth] = useState(0)
  useLayoutEffect(() => {
    const el = outerContainerRef.current
    if (!el) return
    setMeasuredWidth(el.getBoundingClientRect().width)
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setMeasuredWidth(entry.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const scheduleWidth = props.scheduleContainerWidth ?? measuredWidth

  // eventsThisDay is reference-stable; use directly as dep (no JSON.stringify).
  // react-compiler-migration: kept — expensive positionEvents; generic component may not be Compiler-optimized
  const positionedDayEvents = useMemo(
    () =>
      pipe(
        eventsThisDay,
        // Inherit the schedule-level eventDescriptor on any event that
        // doesn't supply its own — matches what HourlySchedule does
        // before positioning so positions match across both call sites.
        R.map((event) => ({
          ...event,
          eventDescriptor:
            event.eventDescriptor || Object.values(defaultEventDescriptor),
        })),
        (events) =>
          positionEvents(
            events,
            [...eventsThisDay, ...(props.columnateDefaultData || [])],
            intervalDuration,
            indentationAmount,
            props.columnateBy,
            sortColumns,
            scheduleWidth,
            minEventWidth,
            columnGroupWeights,
            noOverlap,
          ) as typeof eventsThisDay,
      ),
    [
      eventsThisDay,
      props.columnateBy,
      props.columnateDefaultData,
      intervalDuration,
      indentationAmount,
      sortColumns,
      scheduleWidth,
      minEventWidth,
      defaultEventDescriptor,
      columnGroupWeights,
      noOverlap,
    ],
  )

  return (
    <div
      ref={outerContainerRef}
      className={twMerge(
        'flex max-h-full flex-col overflow-hidden rounded pt-2 relative bg-base-0 text-base-1000 isolate',
        props.className,
      )}
      style={
        {
          '--min-column-width': `calc(${minColumnWidth})`,
          '--min-subcolumn-width': `calc((${minColumnWidth} * ${
            columnHeadings?.length ?? 1
          }) / ${totalCols} )`,
          '--max-column-width': `calc(${maxColumnWidth})`,
          '--max-subcolumn-width': `calc((${maxColumnWidth} * ${
            columnHeadings?.length ?? 1
          }) / ${totalCols} )`,
        } as React.CSSProperties
      }>
      {!eventsThisDay?.length && !props.hideEmptyStatePrompt && (
        <div className='absolute top-0 left-0 right-0 bg-gradient-to-b from-base-0/60 to-transparent z-10 flex flex-col justify-center items-center rounded-lg overflow-hidden'>
          <div className='absolute inset-0 backdrop-blur-sm mask-b-from-0' />
          <div className='flex flex-col gap-4 p-4 items-center text-center relative'>
            <div className='font-semibold text-base-400 text-sm text-centered'>
              No{' '}
              {capitalCase(
                getDefaultEventDescriptor(props.eventDescriptor).plural,
              )}
            </div>
            {!!props.onCreateNew && (
              <Button
                className='rounded-full aspect-square'
                icon={faPlus}
                onClick={() =>
                  props.onCreateNew?.({
                    startTime: new TZDate(
                      pageDate || new Date(),
                      props.timezone,
                    ),
                  })
                }
              />
            )}
          </div>
        </div>
      )}
      <div
        className={twMerge(
          'flex h-max max-h-full flex-col overflow-auto snap-x',
          props.scrollContainerClassName,
        )}>
        <div className='relative flex flex-col shrink min-h-max min-w-fit w-full isolate'>
          {/* displayed columns */}
          <div className='flex flex-col min-h-max min-w-fit'>
            <ColumnHeadings
              columnHeadingStickyTopOffset={columnHeadingStickyTopOffset}
              columnHeadings={columnHeadings}
              className={twMerge(!hideHoursText && 'pl-14')}
              hideHoursText={hideHoursText}
              forceColumnHeadingToHeightOf={forceColumnHeadingToHeightOf}
              positionedEvents={positionedDayEvents}
              minEventWidth={minEventWidth}
              columnGroupWeights={columnGroupWeights}
              totalCols={totalCols}
            />
            {/* Mask the left side as events scroll under */}
            {!hideHoursText && (
              <div className='absolute top-0 left-0 w-full h-full pointer-events-none min-h-max z-30'>
                <div className='sticky left-0 w-14 h-full rounded-l-lg bg-base-0 mask-l-from-0 mask-l-to-4 mask-l-from-transparent mask-l-to-black z-30' />
              </div>
            )}
            {clustersByDay.flatMap((clusters) =>
              clusters?.map((cluster, i) => {
                // Hours skipped between clusters; null when no prior cluster or offset missing.
                const thisClusterStartHour = cluster.pageStartMidnightOffset
                const lastClusterEndHour =
                  clusters[i - 1]?.pageEndMidnightOffset
                const hoursSinceLastCluster =
                  thisClusterStartHour !== undefined &&
                  lastClusterEndHour !== undefined
                    ? thisClusterStartHour - (24 + lastClusterEndHour)
                    : null

                return (
                  <React.Fragment key={i}>
                    {i > 0 &&
                      hoursSinceLastCluster !== null &&
                      hoursSinceLastCluster > 0 && (
                        <div
                          className={twMerge(
                            'text-base-500/20 dark:text-base-1000/60 text-[0.625rem] text-center pt-4 pb-2 font-semibold flex flex-col items-center',
                            !hideHoursText && 'pl-14',
                          )}>
                          <svg className='size-6' viewBox='0 0 10 10'>
                            <path
                              // d='M5,0 C5,2 7,2 7,4 C7,6 3,5 3,7 C3,9 5,8 5,10'
                              d='M5,0 L5,3 L 7,4 L3,6 L5,7 L5,9.5'
                              fill='none'
                              vectorEffect='non-scaling-stroke'
                              stroke='currentColor'
                              strokeWidth='1'
                              strokeLinecap='round'
                            />
                            <path
                              d='M1,9.5 L9,9.5'
                              fill='none'
                              vectorEffect='non-scaling-stroke'
                              stroke='currentColor'
                              strokeWidth='1'
                              strokeLinecap='round'
                            />
                          </svg>
                          <div className='py-1 text-base-500/30 dark:text-base-1000/60'>
                            No Events For {hoursSinceLastCluster} Hours
                          </div>
                          <svg
                            className='size-6 rotate-180'
                            viewBox='0 0 10 10'>
                            <path
                              // d='M5,0 C5,2 7,2 7,4 C7,6 3,5 3,7 C3,9 5,8 5,10'
                              d='M5,0 L5,3 L 7,4 L3,6 L5,7 L5,9.5'
                              fill='none'
                              vectorEffect='non-scaling-stroke'
                              stroke='currentColor'
                              strokeWidth='1'
                              strokeLinecap='round'
                            />
                            <path
                              d='M1,9.5 L9,9.5'
                              fill='none'
                              vectorEffect='non-scaling-stroke'
                              stroke='currentColor'
                              strokeWidth='1'
                              strokeLinecap='round'
                            />
                          </svg>
                        </div>
                      )}
                    <HourlySchedule
                      {...props}
                      key={[
                        cluster.pageStartMidnightOffset,
                        cluster.pageEndMidnightOffset,
                      ].join('-')}
                      pageDate={props.pageDate}
                      // Full day event set seeds columns; ghost columns forwarded to event grid.
                      columnateDefaultData={[
                        ...eventsThisDay,
                        ...(props.columnateDefaultData || []),
                      ]}
                      forceColumnHeadingToHeightOf={
                        columnHeadings?.length
                          ? columnHeadings[0].headingComponent
                          : undefined
                      }
                      events={cluster.events}
                      // Cluster offsets already incorporate defaultPage[Start|End]MidnightOffset;
                      // use them directly — overriding mis-clips empty cross-day clusters.
                      defaultPageStartMidnightOffset={
                        cluster.pageStartMidnightOffset
                      }
                      defaultPageEndMidnightOffset={
                        cluster.pageEndMidnightOffset
                      }
                      interval={intervalDuration}
                      intervalHeight={intervalHeight}
                      indentationAmount={indentationAmount}
                      minEventWidth={minEventWidth}
                      minColumnWidth={minColumnWidth}
                      maxColumnWidth={maxColumnWidth}
                      hideHoursText={hideHoursText}
                      sortColumns={sortColumns}
                      columnHeadingStickyTopOffset={
                        columnHeadingStickyTopOffset
                      }
                      midnightOffset={midnightOffset}
                      positionedDayEvents={positionedDayEvents}
                      columnGroupWeights={columnGroupWeights}
                      totalCols={totalCols}
                      isFirstCluster={i === 0}
                    />
                  </React.Fragment>
                )
              }),
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// typeof cast preserves the generic T; memo prevents reconciling unchanged days on scroll.
export const DaySchedule = React.memo(DayScheduleImpl) as typeof DayScheduleImpl

// Defers onCreateNew one RAF so placeholders mount after the schedule paints.
const useDeferredOnCreateNew = <Fn,>(onCreateNew: Fn | undefined) => {
  const [ready, setReady] = useState(false)
  useEffect(() => {
    if (!onCreateNew) return
    const raf = requestAnimationFrame(() => setReady(true))
    return () => cancelAnimationFrame(raf)
  }, [onCreateNew])
  return ready ? onCreateNew : undefined
}

const HourlyScheduleImpl = <T extends ScheduleEvent>({
  interval: intervalDuration = 15,
  intervalHeight = '1.5rem',
  indentationAmount = 2,
  minEventWidth,
  minColumnWidth,
  maxColumnWidth,
  hideHoursText = false,
  sortColumns = (a, b) => (a > b ? 1 : -1),
  columnHeadingStickyTopOffset,
  forceColumnHeadingToHeightOf,
  positionedDayEvents,
  columnGroupWeights,
  totalCols,
  isFirstCluster,
  ...props
}: StrictOmit<
  DayScheduleProps<T>,
  'clusterDistance' | 'clusters' | 'minEventWidth'
> &
  Required<Pick<DayScheduleProps<T>, 'minEventWidth'>> & {
    // Pre-positioned events for the whole day, computed once at the
    // DaySchedule level. We filter these down to the cluster's time range
    // here instead of re-running positionEvents.
    positionedDayEvents: T[]
    // Per-column-group weights (max concurrent events) for content-aware
    // column allocation. Forwarded to ColumnSeparators so the separator
    // line positions match the events grid.
    columnGroupWeights?: number[]
    // Total grid columns for this schedule, scaled by column-group count.
    // Must match the value used in positionEvents and ColumnHeadings.
    totalCols: number
    // True for topmost cluster; drives whether ColumnSeparators extend up to heading row.
    isFirstCluster: boolean
  }) => {
  // Memoize the normalization so the array reference stays stable across
  // renders when `props.events` reference is stable.
  // react-compiler-migration: kept — referenced in startDayAt/endDayAt/hours deps arrays
  const eventsList = useMemo(
    () => normalizeEventsToTimezone(props.events, props.timezone),
    [props.events, props.timezone],
  )

  const [modal, setModal] = useState<ScheduleModalState<T>>({
    open: false,
    layoutId: '',
    content: null,
  })
  const [eventDisplayed, setEventDisplayed] = useState<T>()

  const componentHash = useId()

  // Memoize for stable identity: unstable ref defeats React.memo on child components.
  // react-compiler-migration: kept — object identity passed to React.memo children
  const defaultEventDescriptor = useMemo(
    () => getDefaultEventDescriptor(props.eventDescriptor),
    [props.eventDescriptor],
  )

  // eventsList is reference-stable, used directly as dep (no JSON.stringify needed).
  // react-compiler-migration: kept — referenced in events's deps array
  const startDayAt = useMemo(
    () => getStartDayAt({ ...props, events: eventsList }),
    [
      eventsList,
      props.timezone,
      props.defaultPageEndMidnightOffset,
      props.defaultPageStartMidnightOffset,
      props.midnightOffset,
      props.pageDate?.toISOString(),
    ],
  )
  // react-compiler-migration: kept — referenced in events's deps array
  const endDayAt = useMemo(
    () => getEndDayAt({ ...props, events: eventsList }),
    [
      eventsList,
      props.timezone,
      props.defaultPageEndMidnightOffset,
      props.defaultPageStartMidnightOffset,
      props.midnightOffset,
      props.pageDate?.toISOString(),
    ],
  )
  const hours = getDayScheduleHours({ ...props, events: eventsList })
  // Filter the day's pre-positioned events down to this cluster's time
  // range. Positioning already happened once at the DaySchedule level
  // (see the comment there); we don't re-run it per cluster.
  const events = rejectEventsOutsideInterval([startDayAt, endDayAt])(
    positionedDayEvents,
  )

  const columnHeadings = getColumnHeadings(
    events,
    props.columnateDefaultData,
    props.columnateBy,
    sortColumns,
  )

  // Defer placeholder mount so thousands of hover-only tiles don't block first paint.
  const onCreateNew = useDeferredOnCreateNew(props.onCreateNew)

  return (
    <>
      <DayScheduleEventDetailsModal
        {...props}
        modalState={[modal, setModal]}
        eventDisplayedState={[eventDisplayed, setEventDisplayed]}
        modalType={props.modalType}
        renderModalContent={props.renderModalContent}
      />
      <div className='flex relative'>
        <RowLines
          columnHeadings={columnHeadings}
          componentHash={componentHash}
          hours={hours}
          intervalDuration={intervalDuration}
          intervalHeight={intervalHeight}
          hideHoursText={hideHoursText}
        />
        {!hideHoursText && (
          <HoursText
            columnHeadings={columnHeadings}
            componentHash={componentHash}
            defaultEventDescriptor={defaultEventDescriptor}
            hours={hours}
            intervalDuration={intervalDuration}
            startDayAt={startDayAt}
            intervalHeight={intervalHeight}
            onCreateNew={onCreateNew}
          />
        )}

        <div className='grid grid-cols-[auto] grid-rows-1 min-h-max shrink grow'>
          {/* Column Separators */}
          <ColumnSeparators
            hideHoursText={hideHoursText}
            columnHeadings={columnHeadings}
            componentHash={componentHash}
            defaultEventDescriptor={defaultEventDescriptor}
            hours={hours}
            intervalDuration={intervalDuration}
            startDayAt={startDayAt}
            intervalHeight={intervalHeight}
            onCreateNew={onCreateNew}
            positionedEvents={positionedDayEvents}
            minEventWidth={minEventWidth}
            columnGroupWeights={columnGroupWeights}
            totalCols={totalCols}
            isFirstCluster={isFirstCluster}
          />

          <ol
            className='col-start-1 row-start-1 pt-2 grid grid-flow-row min-h-max'
            style={{
              // 5-min chunks: 12 per hour × 24 hours = 288 rows.
              gridAutoRows: `calc(${intervalHeight}/${intervalDuration / 5})`,
              maxWidth:
                maxColumnWidth &&
                `calc(var(--max-subcolumn-width) * ${totalCols})`,
              // This needs to be an explicit number so that if we have
              // blank columns, the events don't span across the blank
              // cols. gridAutoCols will not work here.
              gridTemplateColumns: `repeat(${totalCols}, minmax(var(--min-subcolumn-width), 1fr))`,
            }}>
            {events.map((event, i) => (
              <DayScheduleEvent
                key={getDayScheduleLayoutId(
                  props.columnateBy,
                  event,
                  i,
                  componentHash,
                )}
                layoutId={getDayScheduleLayoutId(
                  props.columnateBy,
                  event,
                  i,
                  componentHash,
                )}
                startDayAt={startDayAt}
                endDayAt={endDayAt}
                minDisplayDurationMinutes={15}
                minEventWidth={minEventWidth}
                timezone={props.timezone}
                hideTimezone={props.hideTimezone}
                onClick={() => {
                  if (event.onClick) return event.onClick(event)
                  if (Array.isArray(event.modalContent))
                    return setModal({
                      layoutId: event.name + componentHash + i,
                      content: event.modalContent,
                      open: true,
                    })
                  if (props.renderModalContent) {
                    return setEventDisplayed(event)
                  }
                  return setModal({
                    layoutId: event.name + componentHash + i,
                    content: {
                      title: event.name,
                      content: <DefaultModalContent event={event} />,
                    },
                    open: true,
                  })
                }}
                {...event}
                eventDescriptor={
                  event.eventDescriptor as string | [string, string]
                }
              />
            ))}
            {/*
              Track-sizing placeholders, half of the bidirectional pair
              (the other half lives in ColumnHeadings). One hidden li per
              column heading carries the heading's content at the column-
              group's gridColumn range, so this grid's intrinsic per-track
              min-content includes the heading's intrinsic width. That
              ensures a column with no events still widens enough to fit
              its heading — the column-body min-width matches the
              heading-cell min-width without any JS measurement.
            */}
            {columnHeadings?.map((heading, headingIdx) => {
              const { start, span } = getColumnGroupAllocation(
                columnHeadings.length,
                headingIdx,
                columnGroupWeights,
              )
              return (
                <li
                  key={`col-min-${headingIdx}`}
                  aria-hidden
                  style={{
                    gridColumn: `${start} / span ${span}`,
                    gridRow: 1,
                    height: 0,
                    overflow: 'hidden',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                  }}>
                  {heading.headingComponent}
                </li>
              )
            })}
            {/*
              Indent-zone placeholders: for every staggered/indented event,
              we render an invisible li spanning the cols between its
              parent recursion's left edge and its own colStart with a
              fixed `minWidth`. Without these, CSS Grid satisfies the
              parent event's min-content by piling all its width onto the
              right side of its track range and collapsing the indent cols
              to ~0 — visually erasing the indentation.
            */}
            {(
              events as (T & {
                indentColStart?: number
                indentColWidth?: number
              })[]
            ).map((event, i) =>
              event.indentColStart && event.indentColWidth ? (
                <li
                  key={`indent-${i}`}
                  aria-hidden
                  style={{
                    gridColumn: `${event.indentColStart} / span ${event.indentColWidth}`,
                    gridRow: 1,
                    height: 0,
                    overflow: 'hidden',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    minWidth: indentZoneMinWidthPx + 'px',
                  }}
                />
              ) : null,
            )}
          </ol>
        </div>
      </div>
    </>
  )
}

const HourlySchedule = React.memo(
  HourlyScheduleImpl,
) as typeof HourlyScheduleImpl

const ColumnHeadingsImpl = <T extends ScheduleEvent>(props: {
  columnHeadings: ReturnType<typeof getColumnHeadings>
  className?: string
  hideHoursText: boolean
  columnHeadingStickyTopOffset?: string | number
  forceColumnHeadingToHeightOf: React.ReactNode
  // Pre-positioned events for aria-hidden placeholders at identical gridColumns as the events ol.
  positionedEvents?: (T & {
    colStart?: number
    colWidth?: number
    indentColStart?: number
    indentColWidth?: number
  })[]
  minEventWidth?: number
  columnGroupWeights?: number[]
  totalCols: number
}) => {
  const placeholderColumn = props.forceColumnHeadingToHeightOf
    ? [
        {
          key: 'placeholder-column',
          headingComponent: props.forceColumnHeadingToHeightOf,
        },
      ]
    : undefined

  const columnHeadings = props.columnHeadings ?? placeholderColumn

  if (!columnHeadings) return null

  return (
    <div
      style={{
        top: props.columnHeadingStickyTopOffset,
        // Same template as events `<ol>` so both grids resolve identical per-track widths.
        gridTemplateColumns: `repeat(${props.totalCols}, minmax(var(--min-subcolumn-width), 1fr))`,
      }}
      className={twMerge(
        'text-center grid items-stretch sticky top-0 z-20',
        props.className,
      )}>
      {/*
        Gradient backdrop behind the headings — events scrolling under
        the sticky header fade out via the mask. The absolute position
        fills the grid container's padding edge (its containing block)
        as long as no grid-area is set on this element; specifying
        grid-area would constrain it to a single cell.
      */}
      <div className='absolute inset-0 -bottom-4 pt-2 -mt-2 bg-base-0 rounded-t-lg mask-t-from-0 mask-t-to-4 mask-t-from-transparent mask-t-to-black' />
      {columnHeadings?.map((heading, i) => {
        const { start, span } = getColumnGroupAllocation(
          columnHeadings.length,
          i,
          props.columnGroupWeights,
        )
        return (
          <div
            key={[heading.key].join(' ')}
            // Explicit row 1 so auto-placement doesn't push headings to row 2 behind placeholders.
            style={{ gridColumn: `${start} / span ${span}`, gridRow: 1 }}
            className={twMerge(
              'relative flex justify-center items-center text-sm text-base-500 dark:text-base-1000/60',
              // If there are no actual columns, we're just preserving space so that
              // the rows of this schedule line up with another, so make this
              // heading invisible
              !props.columnHeadings?.length && 'invisible',
              // If there's only one column, visually center the heading in the full
              // space (including the hours legend)
              columnHeadings?.length === 1 && !props.hideHoursText && 'mr-14',
            )}>
            {heading.headingComponent}
          </div>
        )
      })}
      {/*
        Track-sizing placeholders, the matching half of the bidirectional
        pair (the other half lives inside the events `<ol>`). One hidden
        div per real event placed at the event's *actual* gridColumn
        (the colStart/colWidth produced by positionEvents) so this grid's
        per-track sizing sees the same content the events grid does.
      */}
      {props.positionedEvents && props.minEventWidth
        ? props.positionedEvents.map((event, i) => {
            const colStart = event.colStart ?? 1
            const colWidth = event.colWidth ?? props.totalCols
            return (
              <div
                key={`evt-min-${i}`}
                aria-hidden
                style={{
                  gridColumn: `${colStart} / span ${colWidth}`,
                  gridRow: 1,
                  height: 0,
                  overflow: 'hidden',
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  minWidth: props.minEventWidth + 'px',
                }}
              />
            )
          })
        : null}
      {props.positionedEvents?.map((event, i) =>
        event.indentColStart && event.indentColWidth ? (
          <div
            key={`indent-${i}`}
            aria-hidden
            style={{
              gridColumn: `${event.indentColStart} / span ${event.indentColWidth}`,
              gridRow: 1,
              height: 0,
              overflow: 'hidden',
              visibility: 'hidden',
              pointerEvents: 'none',
              minWidth: indentZoneMinWidthPx + 'px',
            }}
          />
        ) : null,
      )}
    </div>
  )
}

const ColumnHeadings = React.memo(
  ColumnHeadingsImpl,
) as typeof ColumnHeadingsImpl

const ColumnSeparatorsImpl = <T extends ScheduleEvent>({
  columnHeadings,
  intervalHeight,
  intervalDuration,
  hours,
  componentHash,
  onCreateNew,
  startDayAt,
  defaultEventDescriptor,
  hideHoursText,
  positionedEvents,
  minEventWidth,
  columnGroupWeights,
  totalCols,
  isFirstCluster,
}: Pick<DayScheduleProps<T>, 'intervalHeight' | 'onCreateNew'> & {
  columnHeadings: ReturnType<typeof getColumnHeadings>
  hours: string[]
  intervalDuration: NonNullable<DayScheduleProps<T>['interval']>
  componentHash: string
  startDayAt: Date | TZDate
  defaultEventDescriptor: ReturnType<typeof getDefaultEventDescriptor>
  hideHoursText: boolean
  // Pre-positioned events for hidden track-sizing placeholders so separator cols match events.
  positionedEvents?: (T & {
    colStart?: number
    colWidth?: number
    indentColStart?: number
    indentColWidth?: number
  })[]
  minEventWidth?: number
  columnGroupWeights?: number[]
  totalCols: number
  // First cluster extends separator up to heading row; lower clusters stay within bounds.
  isFirstCluster: boolean
}) => {
  const cells = columnHeadings || [undefined]
  return (
    <div
      className='col-start-1 col-end-2 row-start-1 grid min-h-max'
      style={{
        gridTemplateColumns: `repeat(${totalCols}, minmax(var(--min-subcolumn-width), 1fr))`,
      }}>
      {cells.map((heading, cellIdx) => {
        const { start, span } = columnHeadings
          ? getColumnGroupAllocation(
              columnHeadings.length,
              cellIdx,
              columnGroupWeights,
            )
          : { start: 1, span: totalCols }
        // Stretch into gap cols so events center visually; first/last chunks stretch one side only.
        const isFirstCell = cellIdx === 0
        const isLastCell = cellIdx === cells.length - 1
        const cellStart = isFirstCell ? start : start - 1
        const cellSpan = span + (isFirstCell ? 0 : 1) + (isLastCell ? 0 : 1)
        return (
          <div
            key={cellIdx}
            className={twMerge(
              'pt-2 grid grid-flow-row relative snap-start',
              !hideHoursText && 'scroll-ml-14',
            )}
            style={{
              gridColumn: `${cellStart} / span ${cellSpan}`,
              gridAutoRows: intervalHeight,
            }}>
            {cellIdx > 0 && (
              // First cluster extends up (-top-2) to meet heading row; lower clusters start at top-2.
              <div
                className={twMerge(
                  'absolute w-px left-0 bottom-0 bg-base-200',
                  isFirstCluster ? '-top-2' : 'top-2',
                )}
              />
            )}
            {hours.map((hour, hourIdx) =>
              Array(60 / intervalDuration)
                .fill(null)
                .map((_, slotIdx) => (
                  <div
                    key={
                      hour +
                      ['col', cellIdx].join('_') +
                      hourIdx +
                      slotIdx +
                      'hour' +
                      componentHash
                    }
                    className='relative'>
                    {!!onCreateNew && (
                      <NewEventPlaceholder
                        placeHolderClasses='z-20'
                        className='absolute inset-0 *:grow'
                        onClick={() =>
                          onCreateNew?.({
                            startTime: addMinutes(
                              startDayAt,
                              (hourIdx * (60 / intervalDuration) + slotIdx) *
                                intervalDuration,
                            ),
                            ...(heading?.defaultCreateData || {}),
                          })
                        }
                        eventDescriptor={defaultEventDescriptor.singular}
                      />
                    )}
                  </div>
                )),
            )}
          </div>
        )
      })}
      {/*
        Track-sizing placeholders so this grid's column tracks resolve to
        the same widths as the headings grid and events `<ol>`. Without
        these, ColumnSeparators's tracks fall back to uniform 1fr
        distribution while the other two grids are content-driven, and
        the vertical separator lines drift away from the column edges.
        We render the same two sets of placeholders both other grids
        carry (heading content + event min-widths).
      */}
      {columnHeadings?.map((heading, headingIdx) => {
        const { start, span } = getColumnGroupAllocation(
          columnHeadings.length,
          headingIdx,
          columnGroupWeights,
        )
        return (
          <div
            key={`sep-hd-${headingIdx}`}
            aria-hidden
            style={{
              gridColumn: `${start} / span ${span}`,
              gridRow: 1,
              height: 0,
              overflow: 'hidden',
              visibility: 'hidden',
              pointerEvents: 'none',
            }}>
            {heading.headingComponent}
          </div>
        )
      })}
      {positionedEvents && minEventWidth
        ? positionedEvents.map((event, i) => {
            const colStart = event.colStart ?? 1
            const colWidth = event.colWidth ?? totalCols
            return (
              <div
                key={`sep-ev-${i}`}
                aria-hidden
                style={{
                  gridColumn: `${colStart} / span ${colWidth}`,
                  gridRow: 1,
                  height: 0,
                  overflow: 'hidden',
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  minWidth: minEventWidth + 'px',
                }}
              />
            )
          })
        : null}
      {positionedEvents?.map((event, i) =>
        event.indentColStart && event.indentColWidth ? (
          <div
            key={`sep-indent-${i}`}
            aria-hidden
            style={{
              gridColumn: `${event.indentColStart} / span ${event.indentColWidth}`,
              gridRow: 1,
              height: 0,
              overflow: 'hidden',
              visibility: 'hidden',
              pointerEvents: 'none',
              minWidth: indentZoneMinWidthPx + 'px',
            }}
          />
        ) : null,
      )}
    </div>
  )
}

const ColumnSeparators = React.memo(
  ColumnSeparatorsImpl,
) as typeof ColumnSeparatorsImpl

const HoursTextImpl = <T extends ScheduleEvent>({
  columnHeadings,
  intervalHeight,
  intervalDuration,
  hours,
  componentHash,
  onCreateNew,
  startDayAt,
  defaultEventDescriptor,
}: Pick<DayScheduleProps<T>, 'intervalHeight' | 'onCreateNew'> & {
  columnHeadings?: ReturnType<typeof getColumnHeadings>
  hours: string[]
  intervalDuration: NonNullable<DayScheduleProps<T>['interval']>
  componentHash?: string
  startDayAt: Date | TZDate
  defaultEventDescriptor: ReturnType<typeof getDefaultEventDescriptor>
}) => {
  return (
    <div
      className='sticky left-0 h-full items-stretch pt-2 grid grid-flow-row min-h-max w-14 z-40 grow-0 shrink-0'
      style={{
        gridAutoRows: intervalHeight,
      }}>
      {hours.map((hour, i) =>
        Array(60 / intervalDuration)
          .fill(null)
          .map((_, j) => (
            <div
              key={hour + i + j + 'hour' + componentHash}
              className='relative border-base-500/10 dark:border-base-1000/30'>
              {!!onCreateNew && (
                <NewEventPlaceholder
                  onClick={() =>
                    onCreateNew?.({
                      startTime: addMinutes(
                        startDayAt,
                        (i * (60 / intervalDuration) + j) * intervalDuration,
                      ),
                      ...(columnHeadings?.[0]?.defaultCreateData || {}),
                    })
                  }
                  eventDescriptor={defaultEventDescriptor.singular}
                />
              )}
              {j === 0 && (
                <div
                  className={twMerge(
                    'sticky pointer-events-none left-0 -mt-2.5 pr-4 text-right text-xs leading-5 text-base-500/50 dark:text-base-1000/60',
                  )}>
                  {hour}
                </div>
              )}
            </div>
          )),
      )}
    </div>
  )
}

export const HoursText = React.memo(HoursTextImpl) as typeof HoursTextImpl

const RowLinesImpl = <T extends ScheduleEvent>({
  intervalHeight,
  intervalDuration,
  hours,
  componentHash,
  hideHoursText,
}: Pick<DayScheduleProps<T>, 'intervalHeight'> & {
  columnHeadings: ReturnType<typeof getColumnHeadings>
  hours: string[]
  intervalDuration: NonNullable<DayScheduleProps<T>['interval']>
  componentHash: string
  hideHoursText: DayScheduleProps<T>['hideHoursText']
}) => {
  return (
    <div className='absolute top-0 left-0 bottom-0 right-0 flex flex-col items-stretch min-h-max'>
      <div
        className='relative grow pt-2 col-start-1 col-end-2 row-start-1 grid grid-flow-row min-h-max'
        style={{
          gridAutoRows: intervalHeight,
        }}>
        {hours.map((hour, i) =>
          Array(60 / intervalDuration)
            .fill(null)
            .map((_, j) => (
              <div
                key={hour + i + j + 'hour' + componentHash}
                className={twMerge(
                  i === 0 && j === 0 && 'border-t',
                  'relative border-b border-base-500/10 dark:border-base-1000/30',
                  !hideHoursText && 'ml-12',
                )}
              />
            )),
        )}
      </div>
    </div>
  )
}

const RowLines = React.memo(RowLinesImpl) as typeof RowLinesImpl

const DefaultModalContent = (
  props: { event: ScheduleEvent } & Pick<
    DayScheduleProps<ScheduleEvent>,
    'timezone' | 'hideTimezone'
  >,
) => (
  <div className='flex flex-col p-4 gap-4'>
    <div className='font-semibold flex flex-wrap gap-1'>
      <div>
        {formatDateRange(
          ['time'],
          [props.event.startTime, props.event.endTime],
          props.timezone ?? props.event.venue?.timezone,
          props.hideTimezone,
        )}
      </div>
      {props.event.venue?.name && <div>at {props.event.venue.name}</div>}
    </div>
    <RenderHTMLContent>{props.event.headline}</RenderHTMLContent>
  </div>
)
